import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface MapUserSettings {
  prefetchWindowSize: number;
  animationSpeed: number; // frames per second (FPS)
}

export const DEFAULT_MAP_SETTINGS: MapUserSettings = {
  prefetchWindowSize: 24,
  animationSpeed: 12, // FPS (equivalent to previous 80ms default)
};

interface MapSettingsContextType {
  settings: MapUserSettings;
  updateSettings: (newSettings: Partial<MapUserSettings>) => void;
  resetToDefaults: () => void;
}

const MapSettingsContext = createContext<MapSettingsContextType | undefined>(undefined);

const STORAGE_KEY = 'jua-map-user-settings';

export function MapSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<MapUserSettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate and merge with defaults
        return {
          ...DEFAULT_MAP_SETTINGS,
          ...parsed,
          // Ensure values are within reasonable bounds
          prefetchWindowSize: Math.max(
            1,
            Math.min(100, parsed.prefetchWindowSize || DEFAULT_MAP_SETTINGS.prefetchWindowSize),
          ),
          animationSpeed: Math.max(
            1,
            Math.min(60, parsed.animationSpeed || DEFAULT_MAP_SETTINGS.animationSpeed),
          ),
        };
      }
    } catch (error) {
      console.warn('Failed to load map settings from localStorage:', error);
    }
    return DEFAULT_MAP_SETTINGS;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save map settings to localStorage:', error);
    }
  }, [settings]);

  const updateSettings = (newSettings: Partial<MapUserSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...newSettings,
      // Ensure values are within reasonable bounds
      prefetchWindowSize: Math.max(
        1,
        Math.min(100, newSettings.prefetchWindowSize ?? prev.prefetchWindowSize),
      ),
      animationSpeed: Math.max(1, Math.min(60, newSettings.animationSpeed ?? prev.animationSpeed)),
    }));
  };

  const resetToDefaults = () => {
    setSettings(DEFAULT_MAP_SETTINGS);
  };

  return (
    <MapSettingsContext.Provider value={{ settings, updateSettings, resetToDefaults }}>
      {children}
    </MapSettingsContext.Provider>
  );
}

export function useMapSettings() {
  const context = useContext(MapSettingsContext);
  if (context === undefined) {
    throw new Error('useMapSettings must be used within a MapSettingsProvider');
  }
  return context;
}
